import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
} from '@chakra-ui/react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import env from 'config/env';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { useEncryption } from 'contexts/EncryptionContext';
import { format } from 'date-fns';
import { FiX, FiRefreshCw, FiCreditCard } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

// Add ExportFormat type before the Profile interface
type ExportFormat = 'json' | 'txt' | 'docx';

interface Profile {
  email: string;
  full_name: string;
  subscription_status?: string;
  subscription_plan?: string;
  subscription_end_date?: string;
  willCancel?: boolean;
  canceledAt?: string;
  effective_status?: string;
}

export default function ProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [fullName, setFullName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('json');
  const { logout, encryptMasterKey } = useAuth();
  const { decrypt } = useEncryption();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${env.API_URL}/profile`);
      setProfile(response.data);
      setFullName(response.data.full_name || '');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not load profile information',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      await axios.put(`${env.API_URL}/profile`, {
        full_name: fullName,
      });

      toast({
        title: 'Success',
        description: 'Profile updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await fetchProfile();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not update profile',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUpdating(true);

    try {
      // Get the master key from localStorage
      const masterKey = localStorage.getItem('masterKey');
      if (!masterKey) {
        throw new Error('Encryption key not found');
      }

      // Re-encrypt master key with new password
      const newEncryptedMasterKey = encryptMasterKey(masterKey, newPassword);

      await axios.put(`${env.API_URL}/profile/password`, {
        currentPassword,
        newPassword,
        newEncryptedMasterKey
      });

      toast({
        title: 'Success',
        description: 'Password updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not update password',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    setIsCanceling(true);

    try {
      await axios.post(`${env.API_URL}/subscription/cancel`);
      toast({
        title: 'Success',
        description: 'Subscription canceled successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchProfile();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not cancel subscription',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCanceling(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!window.confirm('Are you sure you want to resume your subscription?')) {
      return;
    }

    setIsCanceling(true);

    try {
      await axios.post(`${env.API_URL}/subscription/resume`);
      toast({
        title: 'Success',
        description: 'Subscription resumed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchProfile();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not resume subscription',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCanceling(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${env.API_URL}/profile`);
      toast({
        title: 'Success',
        description: 'Account deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      logout();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not delete account',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${env.API_URL}/profile/export`);
      const data = response.data;

      // Decrypt all encrypted fields
      const decryptedData = {
        ...data,
        journalEntries: await Promise.all(data.journalEntries.map(async (entry: any) => ({
          ...entry,
          content: await decrypt(entry.encrypted_content),
          moodDetails: entry.encrypted_mood_details ? await decrypt(entry.encrypted_mood_details) : null,
          moodLabel: entry.encrypted_mood_label ? await decrypt(entry.encrypted_mood_label) : null,
          tags: entry.tag_names ? 
            await Promise.all(entry.tag_names.split(',').map(async (name: string) => 
              await decrypt(name)
            )) : []
        }))),
        tags: await Promise.all(data.tags.map(async (tag: any) => ({
          ...tag,
          name: await decrypt(tag.encrypted_name)
        }))),
        customFields: await Promise.all(data.customFields.map(async (field: any) => ({
          ...field,
          name: await decrypt(field.encrypted_name)
        }))),
        customFieldEntries: await Promise.all(data.customFieldEntries.map(async (entry: any) => ({
          ...entry,
          value: entry.encrypted_value ? await decrypt(entry.encrypted_value) : null,
          name: entry.name ? await decrypt(entry.name) : null
        })))
      };

      // Export based on selected format
      switch (exportFormat) {
        case 'json':
          const jsonBlob = new Blob([JSON.stringify(decryptedData, null, 2)], { type: 'application/json' });
          saveAs(jsonBlob, 'journal_export.json');
          break;

        case 'txt':
          let txtContent = 'Journal Export\n\n';
          decryptedData.journalEntries.forEach((entry: any) => {
            txtContent += `Date: ${new Date(entry.created_at).toLocaleString()}\n\n`;
            txtContent += `${entry.content}\n\n`;
            
            // Add mood if exists
            if (entry.moodLabel) {
              txtContent += `Mood: ${entry.moodLabel}\n`;
            }

            // Add custom field entries for this journal entry
            const entryFields = decryptedData.customFieldEntries
              .filter((fieldEntry: any) => fieldEntry.journal_entry_id === entry.id);
            
            if (entryFields.length > 0) {
              txtContent += 'Custom Fields:\n';
              entryFields.forEach((fieldEntry: any) => {
                const field = decryptedData.customFields.find((f: any) => f.id === fieldEntry.custom_field_id);
                if (field) {
                  txtContent += `- ${field.name}: ${fieldEntry.value || 'N/A'}\n`;
                }
              });
            }

            // Add tags if exist
            if (entry.tags.length) {
              txtContent += `Tags: ${entry.tags.join(', ')}\n`;
            }

            txtContent += '\n';
          });
          const txtBlob = new Blob([txtContent], { type: 'text/plain' });
          saveAs(txtBlob, 'journal_export.txt');
          break;

        case 'docx':
          const doc = new Document({
            sections: [{
              properties: {},
              children: decryptedData.journalEntries.map((entry: any) => {
                const paragraphs = [];

                // Date
                paragraphs.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: `Date: ${new Date(entry.created_at).toLocaleString()}`, bold: true })
                    ]
                  })
                );

                // Content with spacing
                paragraphs.push(
                  new Paragraph({
                    spacing: { before: 300, after: 300 },
                    children: [new TextRun({ text: entry.content })]
                  })
                );

                // Mood
                if (entry.moodLabel) {
                  paragraphs.push(
                    new Paragraph({
                      children: [new TextRun({ text: `Mood: ${entry.moodLabel}`, italics: true })]
                    })
                  );
                }

                // Custom Fields
                const entryFields = decryptedData.customFieldEntries
                  .filter((fieldEntry: any) => fieldEntry.journal_entry_id === entry.id);
                
                if (entryFields.length > 0) {
                  paragraphs.push(
                    new Paragraph({
                      children: [new TextRun({ text: 'Custom Fields:', italics: true })]
                    })
                  );

                  entryFields.forEach((fieldEntry: any) => {
                    const field = decryptedData.customFields.find((f: any) => f.id === fieldEntry.custom_field_id);
                    if (field) {
                      paragraphs.push(
                        new Paragraph({
                          children: [new TextRun({ text: `- ${field.name}: ${fieldEntry.value || 'N/A'}`, italics: true })]
                        })
                      );
                    }
                  });
                }

                // Tags
                if (entry.tags.length) {
                  paragraphs.push(
                    new Paragraph({
                      children: [new TextRun({ text: `Tags: ${entry.tags.join(', ')}`, italics: true })]
                    })
                  );
                }

                // Separator
                paragraphs.push(
                  new Paragraph({
                    spacing: { before: 500, after: 100 }
                  })
                );

                return paragraphs;
              }).flat()
            }]
          });

          const buffer = await Packer.toBlob(doc);
          saveAs(buffer, 'journal_export.docx');
          break;
      }

      toast({
        title: 'Success',
        description: 'Data exported successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxW="container.sm" py={10}>
        <Text>Loading...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.sm" py={10}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Text fontSize="2xl" fontWeight="bold" mb={4}>
            Profile Settings
          </Text>
          <form onSubmit={handleUpdateProfile}>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input value={profile?.email || ''} isReadOnly bg="gray.50" />
              </FormControl>
              <FormControl>
                <FormLabel>Full Name</FormLabel>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                />
              </FormControl>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isUpdating}
              >
                Update Profile
              </Button>
            </Stack>
          </form>
        </Box>

        <Divider />

        <Box>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Change Password
          </Text>
          <form onSubmit={handleUpdatePassword}>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Current Password</FormLabel>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </FormControl>
              <FormControl>
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Confirm New Password</FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </FormControl>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isUpdating}
              >
                Update Password
              </Button>
            </Stack>
          </form>
        </Box>

        <Divider />

        <Box>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Subscription
          </Text>
          {profile?.subscription_status && (
            <Alert 
              status={
                profile.effective_status === 'active' ? 'success' : 
                profile.effective_status === 'canceling' ? 'warning' : 
                'error'
              } 
              mb={4}
              variant="left-accent"
            >
              <AlertIcon />
              <Box>
                <AlertTitle>
                  {profile.effective_status === 'active' && 'Active Subscription'}
                  {profile.effective_status === 'canceling' && 'Subscription Ending Soon'}
                  {profile.effective_status === 'canceled' && 'Subscription Ended'}
                </AlertTitle>
                <AlertDescription>
                  <Stack spacing={1}>
                    <Text>Plan: {profile.subscription_plan}</Text>
                    {profile.effective_status === 'canceling' ? (
                      <>
                        <Text>
                          Your subscription will remain active until{' '}
                          {profile.subscription_end_date && format(new Date(profile.subscription_end_date), 'MMMM d, yyyy')}
                        </Text>
                        <Text>
                          Canceled on: {profile.canceledAt && format(new Date(profile.canceledAt), 'MMMM d, yyyy')}
                        </Text>
                      </>
                    ) : profile.effective_status === 'active' ? (
                      <Text>
                        Next billing date:{' '}
                        {profile.subscription_end_date && format(new Date(profile.subscription_end_date), 'MMMM d, yyyy')}
                      </Text>
                    ) : null}
                  </Stack>
                </AlertDescription>
              </Box>
            </Alert>
          )}
          {profile?.effective_status === 'active' && (
            <Button
              colorScheme="red"
              variant="outline"
              isLoading={isCanceling}
              onClick={handleCancelSubscription}
              leftIcon={<FiX />}
            >
              Cancel Subscription
            </Button>
          )}
          {profile?.effective_status === 'canceling' && (
            <Button
              colorScheme="blue"
              onClick={handleResumeSubscription}
              leftIcon={<FiRefreshCw />}
            >
              Resume Subscription
            </Button>
          )}
          {!profile?.subscription_status && (
            <Button
              colorScheme="blue"
              onClick={() => navigate('/pricing')}
              leftIcon={<FiCreditCard />}
            >
              Upgrade to Premium
            </Button>
          )}
        </Box>

        <Divider />

        <Box>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Export Data
          </Text>
          <Alert status="warning" mb={4}>
            <AlertIcon />
            <Box>
              <AlertTitle>Important Note</AlertTitle>
              <AlertDescription>
                Exported data will be unencrypted and readable. Please store it securely.
              </AlertDescription>
            </Box>
          </Alert>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Export Format</FormLabel>
              <Select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
              >
                <option value="json">JSON (Complete data)</option>
                <option value="txt">Text file (Readable format)</option>
                <option value="docx">Word Document (Formatted)</option>
              </Select>
            </FormControl>
            <Button
              colorScheme="blue"
              onClick={handleExport}
              isLoading={isExporting}
            >
              Export Data
            </Button>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Text fontSize="xl" fontWeight="bold" mb={4} color="red.500">
            Danger Zone
          </Text>
          <Button
            colorScheme="red"
            onClick={handleDeleteAccount}
          >
            Delete Account
          </Button>
        </Box>
      </VStack>
    </Container>
  );
} 